import type { AuditRecord } from "@tdelivr/api-client/generated/security/audit.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the tenant audit board does to the records AUD-01 returns, away from any rendering.
 *
 * The same split as the dispatch console's `offer-monitor.ts`, for the same reason: searching,
 * ordering, paging and deciding what a record's state actually is can all be stated and tested
 * without a browser, and a component that does them inline can only be checked by rendering it.
 *
 * Two things here are not the usual "the design draws a field nobody publishes".
 *
 * **The actor is two different things and the design draws one.** `producerSubject` is whoever
 * *reported* the fact — a service, authenticated with `audit:write` — and `actorChain` is whoever
 * *did* it. They are not the same, they are frequently not even the same kind of thing, and a board
 * that showed the producer under a column headed `Actor` would name the audit client rather than
 * the person. `actorOf()` below reads the chain.
 *
 * **Paging this listing by the only cursor it publishes silently drops rows, on the one page whose
 * closing note promises it does not.** `pageBoundaryHazard()` is the measurement and R-12 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the evidence. Nothing in this module pages the service;
 * that is the finding.
 */

/** AUD-01's published outcome enum, as one name. */
export type AuditOutcome = AuditRecord["outcome"];

/** A link in `actorChain`, as AUD-01 publishes it. */
export type AuditActor = AuditRecord["actorChain"][number];

/**
 * How many records to take in one request.
 *
 * 200 is AUD-01's own maximum: its query schema is `limit: int, min 1, max 200, default 50`. A
 * tenant with more than 200 audit records — which is every tenant, since the design's own KPI
 * reads 8,412 events today — is in a state this screen can only be honest about, and the honest
 * thing is to say the board is the newest 200 rather than the log.
 */
export const PER_FETCH = 200;

/**
 * Whether a record's actor chain contains somebody acting as somebody else.
 *
 * **This is the one thing that makes a tenant audit page different from a log viewer**, and the
 * design's own purpose line says so: "Everything your staff and ours did in your account. Platform
 * staff actions are marked." The mark has a published source — `actorChain[].kind` carries
 * `impersonator` beside `user` and `service` — and the service restricts every row to the verified
 * token tenant, so a chain containing an impersonator on a row in *your* log is exactly the design's
 * `S. Banda · platform staff … cross-tenant`.
 *
 * Any link, not only the first. An impersonator deeper in the chain is still somebody acting as
 * somebody else, and a chain is recorded outermost-in by whoever wrote it — nothing in the contract
 * fixes which end the impersonator sits at.
 */
export function crossTenantActor(record: AuditRecord): boolean {
  return record.actorChain.some((actor) => actor.kind === "impersonator");
}

/**
 * The design's `Actor` cell: who did the thing, not who reported it.
 *
 * **An impersonator in the chain is the actor, and getting this backwards is the one way this
 * column can lie.** The design's third row reads `S. Banda · platform staff` and its badge reads
 * `cross-tenant`: the platform staff member is named, not the tenant user whose identity they
 * borrowed. A cell that drew the innermost link instead would attribute a platform-staff action to
 * the tenant's own administrator — on the page whose whole purpose is "Platform staff actions are
 * marked", and in the one column a reader would quote when asking who did it.
 *
 * Otherwise the innermost link, because that is the actor on whose behalf everything outside it was
 * acting. Its `kind` travels with it: `user` is left unqualified, as the design's `K. Otieno` is,
 * and `service` is named, because a service acting alone is a fact an administrator reading their
 * own log needs on the row.
 *
 * `subject` is an identity subject, not a name. **Nothing on this platform turns a workforce subject
 * into a person's name**: the design draws `K. Otieno` and `A. Mwangi`, and the staff page that
 * would hold those names is one of the twelve with no operation behind it — the federation service
 * links external subjects to providers and holds no name at all. So the subject is shown as
 * published. R-12.
 *
 * An empty chain is possible on the wire — `actorChain` is a required array with no minimum — and
 * means the producer recorded a fact about nobody. Said rather than rendered as an empty cell,
 * because an empty `Actor` reads as a loading row.
 */
export function actorOf(record: AuditRecord): string {
  const impersonator = record.actorChain.find((link) => link.kind === "impersonator");
  const actor = impersonator ?? record.actorChain.at(-1);
  if (actor === undefined) return "no actor recorded";
  if (actor.kind === "user") return actor.subject;
  return `${actor.subject} · ${actor.kind}`;
}

/**
 * The state class and the word, from `outcome` and the actor chain together.
 *
 * The design's three sample rows settle both the mapping and its precedence, which is why this is
 * read off the design rather than decided here:
 *
 * | the design's row                                    | class     | word          |
 * | --------------------------------------------------- | --------- | ------------- |
 * | `payout PYO-0928 approved`                          | `done`    | `recorded`    |
 * | `payout approve refused (same person)`              | `closed`  | `denied`      |
 * | `replay RPL-0311 approved` by `platform staff`      | `caution` | `cross-tenant`|
 *
 * The third row is the one that fixes the precedence: its outcome is a success and its class is
 * `caution`, so being a platform-staff action outranks having succeeded. It does **not** outrank
 * having been refused — the design's second row is a denial by ordinary staff, and a denial is the
 * stronger fact either way: an administrator scanning for what went wrong needs refusals, and a
 * refused cross-tenant action is still a refusal.
 *
 * `failed` has no sample row. AUD-01 publishes three outcomes and the design draws two of them, so
 * `failed` takes the design's `failed` class and the contract's own word — the class is solid and
 * sorts second, which is right for an action that was attempted and did not work.
 */
export function stateOf(record: AuditRecord): { classKey: StateClassKey; label: string } {
  if (record.outcome === "denied") return { classKey: "closed", label: "denied" };
  if (record.outcome === "failed") return { classKey: "failed", label: "failed" };
  if (crossTenantActor(record)) return { classKey: "caution", label: "cross-tenant" };
  return { classKey: "done", label: "recorded" };
}

/**
 * The design's `When` column: a clock time, `09:12:04`.
 *
 * `occurredAt` rather than `ingestedAt`, because `occurredAt` is what the record is *about* and is
 * the column AUD-01 orders by — a board whose rows were timestamped on one clock and sorted on
 * another would look shuffled. Both are published, and the gap between them is the subject of
 * `ingestionLag()` below.
 *
 * Rendered in the viewer's own zone by `Intl`, as a 24-hour clock with seconds, which is what the
 * design writes. The date is not in the design's cell and is not added: the board is the newest 200
 * records and an administrator reading it is reading today.
 */
export function clockTime(record: AuditRecord, zone?: string): string {
  const at = Date.parse(record.occurredAt);
  if (!Number.isFinite(at)) return record.occurredAt;
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(zone === undefined ? {} : { timeZone: zone }),
  }).format(at);
}

/**
 * How long after it happened the log was told, in seconds, or `undefined` when either end is
 * unreadable.
 *
 * Not in the design, and not drawn as a column — it is on the row's title, where an administrator
 * who wonders why a record appears out of order with its neighbours can find it. Both ends are
 * published and the distinction matters on an audit log specifically: `occurredAt` is supplied by
 * whoever reported the fact and is covered by the record's `contentHash`, while `ingestedAt` is
 * stamped by AUD-01 when it received it. A producer with a wrong clock, or one that batches, writes
 * a record that sorts among rows it did not happen between.
 */
export function ingestionLagSeconds(record: AuditRecord): number | undefined {
  const occurred = Date.parse(record.occurredAt);
  const ingested = Date.parse(record.ingestedAt);
  if (!Number.isFinite(occurred) || !Number.isFinite(ingested)) return undefined;
  return Math.round((ingested - occurred) / 1000);
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type AuditOrder = "default" | "state" | "az" | "za";

export interface AuditQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  readonly order: AuditOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface AuditPage {
  readonly rows: readonly AuditRecord[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly AuditRecord[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(record: AuditRecord): string {
  const state = stateOf(record);
  return [
    record.action,
    record.outcome,
    state.label,
    actorOf(record),
    record.actorChain.map((actor) => `${actor.subject} ${actor.kind}`).join(" "),
    record.subject.type,
    record.subject.id,
    record.resource?.type ?? "",
    record.resource?.id ?? "",
    record.correlationId,
    record.eventId,
    clockTime(record),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order AUD-01 answered in — `ORDER BY occurred_at DESC, event_id DESC`, newest
 * first — and the screen must not quietly replace it. An audit log read for a reason is read from
 * the most recent thing backwards.
 *
 * The state filter is over the rows rather than a query parameter, which is the opposite of the
 * offer monitor's choice and for the opposite reason: AUD-01's listing takes `limit` and `before`
 * and **no filter at all**, so there is no request that would narrow it. What that costs is stated
 * on the board rather than hidden — see `boundedNote`.
 */
export function auditPage(
  rows: readonly AuditRecord[],
  query: AuditQuery,
  state?: string,
): AuditPage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (record) =>
      (state === undefined || stateOf(record).label === state) &&
      (needle.length === 0 || searchableText(record).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          // "Name" is the first column, as the design's A–Z is. Here that is the clock.
          const compared = left.occurredAt.localeCompare(right.occurredAt);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The states present in what came back, for the design's filter.
 *
 * In the order they first appear on the board, which is what the design's own `gridUI` does —
 * `[...new Set(items.map(x => x.state))]` over the rows in board order. Not the state classes'
 * sort order, which is this console's idiom elsewhere and is the more useful of the two: the design
 * is the rule, the board is ordered newest-first, and a filter listing its options in the order an
 * administrator met them reads the way the board does.
 */
export function statesPresent(rows: readonly AuditRecord[]): readonly string[] {
  return [...new Set(rows.map((record) => stateOf(record).label))];
}

/**
 * Whether paging this listing with the only cursor it publishes would skip a record.
 *
 * **The finding this screen exists to record.** AUD-01 orders by `(occurred_at DESC, event_id
 * DESC)` and its one cursor is `before`, a bare `date-time`, applied as `occurred_at < $before`. So
 * a second page asked for with the last row's `occurredAt` begins strictly before that instant —
 * and every record sharing it, including ones this page did not show, is skipped. There is no
 * `nextBefore` in the response either, so the cursor has to be synthesised from the last row, which
 * is what makes it unsafe rather than merely awkward.
 *
 * Audit records sharing an instant are not hypothetical: `occurredAt` is supplied by the producer,
 * a producer recording two facts about one decision stamps both from the same clock read, and the
 * platform's audit writes come in bursts under one `correlationId`. This function says whether the
 * page in hand ends inside such a burst, which is the case where paging would lose a row.
 *
 * It is the same defect the exceptions and manifest listings were fixed for — "paging on a composite
 * key so a shared timestamp cannot hide one" — on the one listing where a lost row is a false
 * statement rather than a gap: the design's closing note reads "Append-only. Platform staff actions
 * on your account always appear here", and a page that quietly dropped one would make that sentence
 * untrue while looking complete.
 *
 * @returns the shared instant, when the last row of a full page shares its `occurredAt` with the
 * row before it; `undefined` when paging would be safe or there is nothing more to page.
 */
export function pageBoundaryHazard(
  rows: readonly AuditRecord[],
  perFetch: number = PER_FETCH,
): string | undefined {
  // A short page is the whole log: there is nothing after it to page to, so there is nothing to
  // lose. The hazard is a property of continuing, not of the rows.
  if (rows.length < perFetch) return undefined;
  const last = rows.at(-1);
  const previous = rows.at(-2);
  if (last === undefined || previous === undefined) return undefined;
  return last.occurredAt === previous.occurredAt ? last.occurredAt : undefined;
}

/**
 * The one or two sentences this board owes an administrator about what it actually fetched.
 *
 * The design gives the grid no such line. The dispatch console's two boards added one for the
 * reason R-08 defends — an operator working a partial board while believing it whole is the failure
 * it exists to prevent — and on an audit log that reason is at its strongest, because the page's own
 * purpose is completeness.
 *
 * Two facts, in order of how much they change what an administrator should conclude:
 *
 *  1. **The board is the newest 200, and there is no second page.** AUD-01's cursor cannot be
 *     followed safely (`pageBoundaryHazard`), so this screen does not follow it. An administrator
 *     who needs older records needs the fix in R-12, not a Next button that loses rows.
 *  2. **The filter and the search are over those 200 only.** The listing takes no filter, so
 *     choosing `denied` narrows what is on screen rather than asking the service for denials.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param hazard the instant `pageBoundaryHazard` found, when the page ends inside a burst.
 * @param perFetch the limit asked for, so a full page can be told from a complete log.
 */
export function boundedNote(
  fetched: number | undefined,
  hazard?: string,
  perFetch = PER_FETCH,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (fetched >= perFetch) {
    const sentence = `This is the newest ${perFetch} records, which is the most AUD-01 answers in one request. The search and the state filter cover these ${perFetch} and not the whole log.`;
    return hazard === undefined
      ? sentence
      : `${sentence} It also ends inside a group of records sharing ${hazard}, and AUD-01's only cursor is a plain instant — so asking for the next page would skip the rest of that group. This board does not ask. See R-12.`;
  }
  return "AUD-01 takes no search or state filter, so both narrow the records already on screen rather than asking the service.";
}
